import { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useAuth } from "react-oidc-context";
import { cn } from "@/lib/utils";
import { getDisplayName, getInitials } from "@/lib/userDisplay";
import { useSettingsModal } from "@/contexts/SettingsModalContext";
import { UserPanelContent } from "./UserPanelContent";

type Side = "top" | "bottom" | "left" | "right";
type Align = "start" | "center" | "end";
type Variant = "default" | "transparent";

interface UserProfileProps {
  /** Which side of the trigger the panel opens on. Default: "top" */
  side?: Side;
  /** Alignment along the side axis. Default: "start" */
  align?: Align;
  /** Visual variant of the trigger. Default: "default" */
  variant?: Variant;
  onOpenSettings?: () => void;
}

export function UserProfile({
  side = "top",
  align = "start",
  variant = "default",
  onOpenSettings,
}: UserProfileProps = {}) {
  const [open, setOpen] = useState(false);
  const { openSettingsModal } = useSettingsModal();

  const auth = useAuth();
  const profile = auth.user?.profile;

  const name = getDisplayName(profile);
  const initials = getInitials(profile);

  const close = () => setOpen(false);

  const openSettings = () => {
    setOpen(false);
    onOpenSettings?.();
    openSettingsModal();
  };

  const isTransparent = variant === "transparent";

  return (
    <DropdownMenu.Root modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={cn(
            "group flex h-8 w-full items-center gap-2.5 rounded-lg px-2 transition-colors",
            "focus:outline-none focus:ring-2",
            isTransparent
              ? [
                  "bg-transparent text-on-chrome",
                  "hover:bg-white/10 hover:text-white",
                  "focus:ring-white/30",
                ]
              : [
                  "text-secondary",
                  "hover:bg-surface-elevated hover:text-primary",
                  "focus:ring-brand-cyan/30",
                ],
          )}
          aria-label="User menu"
        >
          <span
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold tracking-wide transition-colors",
              isTransparent
                ? "bg-white/15 text-on-chrome group-hover:bg-white/20 group-hover:text-white"
                : "bg-surface-elevated text-secondary group-hover:text-primary",
            )}
          >
            {initials}
          </span>

          <span className="min-w-0 flex-1 truncate text-left text-sm font-medium">{name}</span>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={side}
          align={align}
          sideOffset={8}
          collisionPadding={12}
          className="z-50 w-72 overflow-hidden rounded-xl border border-border bg-surface shadow-xl shadow-black/20"
        >
          <UserPanelContent onClose={close} onOpenSettings={openSettings} />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
